import axios from 'axios';
import { 
  MealPlan, 
  MealPlanRequest, 
  UserPreferences, 
  ApiResponse, 
  Ingredient,
  AutoSaveBehavior,
  AutoSaveStatus
} from '../types/mealPlanner';

// Use relative URLs in development (with proxy) and full URLs in production
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? '' // Use proxy in development
  : (process.env.REACT_APP_API_URL || 'http://localhost:5000');

// Simple Axios configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000
});

// Add request interceptor to automatically include Authorization header and anonymous session ID
api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    const anonymousSessionId = localStorage.getItem('anonymousSessionId');
    
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      console.log('🔐 [API] Adding Authorization header for request to:', config.url);
    } else if (anonymousSessionId) {
      // For anonymous users, include the session ID in headers
      // The session ID from localStorage already includes the 'anon_' prefix
      config.headers['x-anonymous-session-id'] = anonymousSessionId;
      console.log('🔍 [API] Adding anonymous session ID header for request to:', config.url, 'Session ID:', anonymousSessionId);
    } else {
      console.log('⚠️ [API] No access token or anonymous session ID found for request to:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is 401 and we haven't already tried to refresh the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if this is an anonymous user (no access token)
      const accessToken = localStorage.getItem('accessToken');
      const anonymousSessionId = localStorage.getItem('anonymousSessionId');
      
      // If no access token but we have an anonymous session, don't try to refresh
      if (!accessToken && anonymousSessionId) {
        console.log('🔍 [API] Anonymous user with 401 error, not attempting token refresh');
        return Promise.reject(error);
      }
      
      // Only attempt token refresh if we have an access token
      if (accessToken) {
        originalRequest._retry = true;
        
        try {
          console.log('🔄 [API] Token expired, attempting to refresh...');
          
          // Create a separate axios instance for refresh to avoid circular references
          const refreshApi = axios.create({
            baseURL: API_BASE_URL,
            headers: { 'Content-Type': 'application/json' },
            withCredentials: true,
          });
          
          // Refresh token is stored as an HTTP-only cookie, so we don't need to send it in the body
          const refreshResponse = await refreshApi.post('/api/auth/refresh');
          
          if (refreshResponse.data.success && refreshResponse.data.accessToken) {
            console.log('✅ [API] Token refreshed successfully');
            
            // Update the access token in localStorage
            localStorage.setItem('accessToken', refreshResponse.data.accessToken);
            
            // Update the Authorization header for the original request
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.accessToken}`;
            
            // Retry the original request with the new token
            return api(originalRequest);
          } else {
            throw new Error('Token refresh response invalid');
          }
        } catch (refreshError) {
          console.error('💥 [API] Token refresh error:', refreshError);
          // Token refresh failed, clear the access token and redirect to login
          localStorage.removeItem('accessToken');
          window.location.href = '/auth';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export class MealPlannerAPI {
  // Reroll a meal within a meal plan
  static async rerollMeal(request: { weekStartDate: string; dayIndex: number; mealIndex: number; userPreferences: any; anonymousSessionId?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/reroll', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to reroll meal'
      };
    }
  }

  // Reroll an individual meal (for meal suggestions, etc.)
  static async rerollIndividualMeal(request: { userId: number; mealId: number; reason?: string; context?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meals/reroll', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to reroll individual meal'
      };
    }
  }

  // Generate meal plan
  static async generateMealPlan(request: MealPlanRequest): Promise<ApiResponse<{ mealPlan: MealPlan }>> {
    try {
      const response = await api.post<ApiResponse<{ mealPlan: MealPlan }>>('/api/meal-plans/generate', request);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to generate meal plan'
      };
    }
  }

  /**
   * Save a meal plan as a reference-based structure
   * This reduces storage size from ~57KB to ~2-5KB
   */
  static async saveMealPlan(weekStartDate: string, mealPlan: MealPlan, userPreferences?: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/save', {
        weekStartDate,
        mealPlanData: mealPlan,
        userPreferences
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to save meal plan'
      };
    }
  }

  /**
   * Save an anonymous meal plan
   */
  static async saveAnonymousMealPlan(weekStartDate: string, mealPlan: MealPlan, userPreferences?: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/save', {
        weekStartDate,
        mealPlanData: mealPlan,
        userPreferences
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to save anonymous meal plan'
      };
    }
  }

  /**
   * Update only the grocery list state (efficient partial update)
   */
  static async updateGroceryListState(weekStartDate: string, groceryListUpdates: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/update-grocery-list', {
        weekStartDate,
        groceryListUpdates
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to update grocery list'
      };
    }
  }

  /**
   * Update only the side dish selections (efficient partial update)
   */
  static async updateSideDishSelections(weekStartDate: string, mealId: number, sideDishIds: number[]): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/update-side-dishes', {
        weekStartDate,
        mealId,
        sideDishIds
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to update side dishes'
      };
    }
  }

  // Get current meal plan
  static async getCurrentMealPlan(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>('/api/meal-plans/current');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get current meal plan'
      };
    }
  }

  // Get meal plan by week
  static async getMealPlanByWeek(weekStartDate: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>(`/api/meal-plans/week/${weekStartDate}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get meal plan by week'
      };
    }
  }

  // Get all user meal plans
  static async getUserMealPlans(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>('/api/meal-plans/all');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get user meal plans'
      };
    }
  }

  // Delete meal plan
  static async deleteMealPlan(weekStartDate: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.delete<ApiResponse<any>>(`/api/meal-plans/week/${weekStartDate}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to delete meal plan'
      };
    }
  }

  // Copy meal plan to a new week
  static async copyMealPlanToWeek(sourceWeekStartDate: string, targetWeekStartDate: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/copy', {
        sourceWeekStartDate,
        targetWeekStartDate
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to copy meal plan'
      };
    }
  }

  // Get user's meal plan history (paginated)
  static async getUserMealPlanHistory(page: number = 1, limit: number = 20): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>(`/api/meal-plans/history?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get meal plan history'
      };
    }
  }

  // Get user's meal plan statistics
  static async getUserMealPlanStats(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>('/api/meal-plans/stats');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get meal plan statistics'
      };
    }
  }

  // Get last meal plan for anonymous user
  static async getLastAnonymousMealPlan(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>('/api/meal-plans/current');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get last anonymous meal plan'
      };
    }
  }

  // Save user preferences
  static async saveUserPreferences(id: string, preferences: UserPreferences): Promise<ApiResponse<void>> {
    try {
      const response = await api.post<ApiResponse<void>>(`/api/user-preferences/${id}`, preferences);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to save user preferences'
      };
    }
  }

  // Get user preferences
  static async getUserPreferences(id: string): Promise<ApiResponse<UserPreferences>> {
    try {
      const response = await api.get<ApiResponse<UserPreferences>>(`/api/user-preferences/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get user preferences'
      };
    }
  }

  // Get default user preferences
  static async getDefaultPreferences(id: string): Promise<ApiResponse<UserPreferences>> {
    try {
      const response = await api.get<ApiResponse<UserPreferences>>(`/api/user-preferences/${id}/default`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get default preferences'
      };
    }
  }

  // Side Dish Operations - Updated to handle ingredients properly
  static async rerollSideDish(request: {
    mealId: number;
    currentSideDishId?: number;
    weekStartDate: string;
    userPreferences?: any;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/side-dishes/reroll', request);
      
      if (response.data.success && response.data.data) {
        const updatedMeal = response.data.data;
        
        // Debug: Log what we're getting from the backend
        console.log('🔍 [API] Backend response for rerollSideDish:', {
          success: response.data.success,
          hasData: !!response.data.data,
          mealId: updatedMeal?.id,
          sideDishCount: updatedMeal?.sideDishes?.length || 0,
          ingredientCount: updatedMeal?.ingredients?.length || 0,
          sideDishes: updatedMeal?.sideDishes,
          ingredients: updatedMeal?.ingredients
        });
        
        // The backend should already have merged ingredients, so we don't need to modify them

        return {
          success: true,
          data: updatedMeal,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to reroll side dishes',
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to reroll side dishes'
      };
    }
  }

  static async addSideDish(request: {
    mealId: number;
    weekStartDate: string;
    userPreferences?: any;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/side-dishes/add', request);
      
      if (response.data.success && response.data.data) {
        const updatedMeal = response.data.data;
        
        // Debug: Log what we're getting from the backend
        console.log('🔍 [API] Backend response for addSideDish:', {
          success: response.data.success,
          hasData: !!response.data.data,
          mealId: updatedMeal?.id,
          sideDishCount: updatedMeal?.sideDishes?.length || 0,
          ingredientCount: updatedMeal?.ingredients?.length || 0,
          sideDishes: updatedMeal?.sideDishes,
          ingredients: updatedMeal?.ingredients
        });
        
        // The backend should already have merged ingredients, so we don't need to do it again
        // Just return the updated meal as-is

        return {
          success: true,
          data: updatedMeal,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to add side dish',
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to add side dish'
      };
    }
  }

  static async removeSideDish(request: {
    mealId: number;
    weekStartDate: string;
    userPreferences?: any;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/side-dishes/remove', request);
      
      if (response.data.success && response.data.data) {
        const updatedMeal = response.data.data;
        
        // Debug: Log what we're getting from the backend
        console.log('🔍 [API] Backend response for removeSideDish:', {
          success: response.data.success,
          hasData: !!response.data.data,
          mealId: updatedMeal?.id,
          sideDishCount: updatedMeal?.sideDishes?.length || 0,
          ingredientCount: updatedMeal?.ingredients?.length || 0,
          sideDishes: updatedMeal?.sideDishes,
          ingredients: updatedMeal?.ingredients
        });
        
        // The backend should already have the correct ingredients, so we don't need to modify them

        return {
          success: true,
          data: updatedMeal,
        };
      } else {
        return {
          success: false,
          error: response.data.error || 'Failed to remove side dish',
        };
      }
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to remove side dish'
      };
    }
  }

  // Admin: Generate Meal Suggestions
  static async generateMealSuggestions(preferences: UserPreferences, count: number = 5): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/admin/generate-meal-suggestions', { preferences, count });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to generate meal suggestions'
      };
    }
  }

  // Admin: Get Pending Meals
  static async getPendingMeals(page: number = 1, limit: number = 10): Promise<ApiResponse<any>> {
    try {
      const response = await api.get<ApiResponse<any>>(`/api/admin/meals/pending?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get pending meals'
      };
    }
  }

  // Admin: Review a Meal
  static async reviewMeal(mealId: number, isApproved: boolean, feedback?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>(`/api/admin/meals/${mealId}/review`, { isApproved, feedback });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to review meal'
      };
    }
  }

  // Admin: Save a Generated Meal
  static async saveGeneratedMeal(meal: any): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/admin/meals/save-generated', meal);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to save generated meal'
      };
    }
  }

  static generateGroceryList(mealPlan: MealPlan): Ingredient[] {
    const groceryMap = new Map<string, Ingredient>();

    // Process main meal ingredients
    mealPlan.days.forEach((day) => {
      if (!day.meals || !Array.isArray(day.meals)) {
        console.warn('Warning: day.meals is not an array or is undefined:', day);
        return;
      }
      
      day.meals.forEach((meal) => {
        if (!meal) {
          console.warn('Warning: meal is undefined or null');
          return;
        }
        
        if (meal.ingredients && Array.isArray(meal.ingredients)) {
          meal.ingredients.forEach((ingredient) => {
            if (!ingredient || !ingredient.name) {
              console.warn('Warning: ingredient or ingredient.name is undefined:', ingredient);
              return;
            }
            
            try {
              const key = ingredient.name.toLowerCase();
              const existing = groceryMap.get(key);

              if (existing) {
                existing.amount = (existing.amount || 0) + (ingredient.amount || 0);
              } else {
                groceryMap.set(key, {
                  ...ingredient
                });
              }
            } catch (error) {
              console.error('Error processing ingredient:', ingredient, error);
            }
          });
        }

        // Process side dish ingredients
        if (meal.sideDishes && Array.isArray(meal.sideDishes)) {
          meal.sideDishes.forEach((sideDish) => {
            if (!sideDish) {
              console.warn('Warning: sideDish is undefined or null');
              return;
            }
            
            if (sideDish.ingredients && Array.isArray(sideDish.ingredients)) {
              sideDish.ingredients.forEach((ingredient) => {
                if (!ingredient || !ingredient.name) {
                  console.warn('Warning: side dish ingredient or ingredient.name is undefined:', ingredient);
                  return;
                }
                
                try {
                  const key = ingredient.name.toLowerCase();
                  const existing = groceryMap.get(key);

                  if (existing) {
                    existing.amount = (existing.amount || 0) + (ingredient.amount || 0);
                  } else {
                    groceryMap.set(key, {
                      ...ingredient
                    });
                  }
                } catch (error) {
                  console.error('Error processing side dish ingredient:', ingredient, error);
                }
              });
            }
          });
        }
      });
    });

    return Array.from(groceryMap.values());
  }

  // Auto-save methods
  static async getAutoSaveStatus(): Promise<ApiResponse<{ autoSaveStatus: AutoSaveStatus }>> {
    try {
      const response = await api.get<ApiResponse<{ autoSaveStatus: AutoSaveStatus }>>('/api/meal-plans/auto-save/status');
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to get auto-save status'
      };
    }
  }

  static async updateAutoSaveBehavior(behavior: AutoSaveBehavior): Promise<ApiResponse<any>> {
    try {
      const response = await api.post<ApiResponse<any>>('/api/meal-plans/auto-save/behavior', { behavior });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: error.message || 'Failed to update auto-save behavior'
      };
    }
  }
}

// Email verification
export const verifyEmail = async (token: string): Promise<ApiResponse<any>> => {
  try {
    const response = await api.post(`/api/auth/verify-email/${token}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Verification failed');
    }
    throw error;
  }
};

// Resend email verification
export const resendEmailVerification = async (email: string): Promise<ApiResponse<any>> => {
  try {
    const response = await api.post('/api/auth/resend-verification', { email });
    return response.data;
  } catch (error: any) {
    if (error.response?.data) {
      throw new Error(error.response.data.message || 'Failed to resend verification');
    }
    throw error;
  }
};

export default api;
